import deepmerge from 'deepmerge';
import type { Config, DeepPartial } from '../default/types';
import defaultConfig, { getFullLocaleFromConfig, environment, customArrayMerge } from '../default';
import { productConfig } from './product/product';
import { installmentCalculatorConfig } from './product/installmentCalculator';
import { dynamicHelp } from './cms/dynamicHelp';

const tenantId = 'universalat';

const config = deepmerge<Config, DeepPartial<Config>>(
  defaultConfig,
  {
    host: { name: tenantId, domain: 'universal.at', appDeeplinkProtocol: 'uvsapp' },
    clientId: 'UniversalAt',
    i18n: {
      defaultLocale: 'de-AT',
      locales: ['de-AT'],
      languages: ['de'],
    },
    tenantId,
    company: {
      name: 'Universal',
      seoTitleName: 'Universal.at',
    },
    timeZone: 'Europe/Vienna',
    headerSlots: {
      joe: true,
      serviceApi: `https://storage.googleapis.com/drupal-pages/${environment}/menus/${tenantId}/<locale>/bucket-service-flyout.json`,
    },
    assets: {
      cmsImage: {
        imageOptimizationEnabled: true,
        uriTransformEndpoint: `https://storage.googleapis.com/drupal-images-${tenantId}/$1/`,
      },
      assetServerHost: 'https://bilder.universal.at',
    },
    staticContent: {
      apiEndpoints: {
        webappJson: `https://cms.${tenantId}.empirie.<envTld>`,
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    search: {
      adServer: {
        mode: 'containerOnly',
      },
    },
    middleware: {
      cmsRewrites: {
        exact: ['/', '/storefront', '/mafo/unsubscribe', '/uwg7/unsubscribe'],
        prefixes: [
          '/service-hilfe',
          '/lp',
          '/magazin',
          '/node',
          '/servicelayer/',
          '/preview-app/',
          '/mailings/',
        ],
      },
    },
    abTesting: {
      clientSideEnrollment: {
        activeTestIds: [
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
            ? '2025_4_Artikelreduktion_test'
            : '2025_4_Artikelreduktion',
        ],
      },
    },
    product: productConfig,
    installmentCalculator: installmentCalculatorConfig,
    header: {
      desktopNavigationMode: 'hover',
    },
    order: {
      joe: {
        enabled: true,
      },
      premium: {
        enabled: false,
      },
      seals: {
        staatswappen: {
          enabled: true,
          url: 'http://www.staatswappen.at/',
        },
        handelsverband: {
          enabled: true,
          url: 'https://www.handelsverband.at/trustmark-austria/',
        },
        oeonline: {
          enabled: true,
          url: '/service-hilfe/ueber-uns/auszeichnungen/',
        },
      },
      assets: {
        premium: {
          getPremiumImage: ({ device }: { device: string }) =>
            `https://bilder.universal.at/empiriecom/io/universalat/de/BS3_CheckOut_${device}.jpg`,
          getPremiumIcon: () =>
            'https://bilder.universal.at/empiriecom/io/universalat/de/premiumicon_1.svg',
        },
        gifs: {
          success: {
            url: 'https://bilder.universal.at/i/empiriecom/io/de/thank_you_1_transparent.gif',
          },
        },
      },
      hasFlexikontoAdvantageClub: true,
    },
    footer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-pages/${environment}<subfolder>/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    forms: {
      contact: { dynamicHelp },
      newsletterUnsubscribe: {
        useDedicatedSubmitButtonTitleFor: ['unsubscribe|99', 'unsubscribeFlexikonto|98'],
        fields: [
          {
            id: 'newsletter',
            formType: 'radio',
            defaultValue: 'unsubscribe|99',
            options: [
              {
                id: 'frq-daily',
                value: 'frequency|0',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.daily',
              },
              {
                id: 'frq-weekly',
                value: 'frequency|1',
                messageKey: 'forms.newsletter.unsubscribe.type.frequency.option.weekly',
              },
              {
                id: 'unsubscribe',
                value: 'unsubscribe|99',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribe',
              },
              {
                id: 'unsubscribe-flexikonto',
                value: 'unsubscribeFlexikonto|98',
                messageKey: 'forms.newsletter.unsubscribe.type.unsubscribeFlexikonto',
              },
              {
                id: 'pause',
                value: 'pause|-100',
                messageKey: 'forms.newsletter.unsubscribe.type.pause',
              },
            ],
            additionalForm: {
              id: 'pause',
              formType: 'select',
              type: 'pause',
              showOnlyWhenWhatSelected: 'pause',
              messageKeyPlaceholder: 'forms.newsletter.unsubscribe.type.pause.label',
              defaultValue: '7',
              options: [
                {
                  id: 'pause-7',
                  value: '7',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.7',
                },
                {
                  id: 'pause-14',
                  value: '14',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.14',
                },
                {
                  id: 'pause-21',
                  value: '21',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.21',
                },
                {
                  id: 'pause-30',
                  value: '30',
                  messageKey: 'forms.newsletter.unsubscribe.type.pause.option.30',
                },
              ],
            },
          },
        ],
      },
      newsletterSubscribe: {
        de: {
          unsubscribeLink: 'https://www.universal.at/service-hilfe/newsletter/abmelden/',
          voucherLink: 'https://www.universal.at/service-hilfe/gutscheine-rabatte/',
        },
      },
    },
    banner: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    nlSheetTexts: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/nl-sheet.json`,
      },
    },
    serviceLayer: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<locale>/<identifier>.json`,
        bucketPages: `https://storage.googleapis.com/drupal-pages/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    seoContent: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/inspire-seofiles/<locale>_${tenantId}-<category>.json`,
      },
    },
    survey: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/drupal-surveys/${environment}/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    promotionBanner: {
      apiEndpoints: {
        bucketEmptyPromotions: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/empty-promotion.json`,
        bucketPromotion: `https://storage.googleapis.com/promotion-data/${tenantId}/<language>/<code>.json`,
        bucketContentSnippet: `https://storage.googleapis.com/drupal-contentsnippet/${environment}/${tenantId}/<language>/<identifier>.json`,
      },
    },
    voucher: {
      apiEndpoints: {
        bucket: `https://storage.googleapis.com/promotion-data/${tenantId}/<locale>/<identifier>.json`,
      },
    },
    appBanner: { appUrl: 'https://app.adjust.com/7sq21cg_3c6m099' },
    tracking: {
      categoryIdHomepage: '137398',
      soastaId: 'VCRLH-5E9R4-TT9EZ-675H5-A9TNA',
      tagmanagerId: 'GTM-PZZ54R2',
      googleSiteVerificationID: ['s2QUnwdvTe-7ynyeA5_mieNSFv5fBht-tPHc8U2_BOs'],
      msvalidateID: '4067315BE5669CD6C74BDEC6F6AC5D47',
      pinterestRichPinVerificationID: 'aa7a73f8932f46f1c78af391ae7e3d8e',
      schemaOrgOrganisation: {
        'de-AT': {
          addressCountry: 'AT',
          addressRegion: 'SBG',
          addressLocality: 'Salzburg',
          alternateName: ['UNIVERSAL', 'Universal Versand', 'Universalversand'],
          areaServed: 'AT',
          contactEmail: 'service@universal.at',
          contactUrl: 'https://www.universal.at/service-hilfe/kontakt-beratung',
          description:
            'Möbel, Mode, Schuhe & Technik entdecken. UNIVERSAL ist 1957 als Anbieter von Optik und Fotoartikeln gestartet, mittlerweile hat sich UNIVERAL zu einer traditionellen österreichischen Marke entwickelt.',
          email: 'service@universal.at',
          founder: 'Hanns Porst',
          foundingDate: '1957-01-01',
          foundingLocation: 'Nürnberg',
          hoursAvailable: ['Mo-Su 7:00-22:00'],
          legalName: 'Universal Versand GmbH',
          logo: `/assets/logos/${tenantId}/logo.svg`,
          memberOf: 'ottogroup',
          name: 'Universal',
          productSupported: '',
          sameAs: [
            'https://www.facebook.com/UniversalVersand',
            'https://www.instagram.com/universal.versand/',
            'https://www.pinterest.com/universal1/',
            'https://www.youtube.com/user/UniversalVersand1',
            'https://de.wikipedia.org/wiki/Universal_Versand',
          ],
          slogan: 'Jeder hat sein Schön...',
          telephone: '+43-662-4485-8',
          url: 'https://www.universal.at',
          postalCode: '5020',
          privacyNoticePath: '/service-hilfe/ueber-uns/datenschutz',
          potentialAction: {
            target: 'https://www.universal.at/s/',
          },
          streetAddress: 'Peilsteinerstraße 5-7',
        },
      },
    },
    externalScript: {
      kuecheCo: {
        key: 'pAYrueynYRcqna7lGLoVT39kF83Wy3PYENp81Yw0QJqeTyzAjXvc1FwGOzCm',
      },
    },
    headerInformation: {
      de: {
        description:
          'Universal.at - Online Shop für Mode, Möbel, Technik ✓ Gratis 3 Jahre XXL-Garantie ✓ Wunsch-Teilzahlung ✓ Gratis Versand ab EUR 75.- (ausgen. Spedition)',
        title: 'Universal.at | Mode - Möbel - Technik online kaufen',
        canonical: 'https://www.universal.at',
        robots: 'index,follow',
      },
    },
    // kim is renamed as "customerCentricFeedback"
    customerCentricFeedback: {
      tenantId: '1100',
    },
    dynamicYieldTracking: {
      key: 'bedfbdea4d3d014a8b838c361b7e67a5e683f21ac62f094f2dd353b478251146',
    },
    geoCountryLayer: {
      enabled: true,
      flag: 'at',
      alternatives: [
        {
          flag: 'de',
          shops: [
            { link: 'https://www.baur.de', title: 'baur.de' },
            { link: 'https://www.quelle.de', title: 'quelle.de' },
          ],
        },
        {
          flag: 'ch',
          shops: [{ link: 'https://www.ackermann.ch', title: 'ackermann.ch' }],
        },
      ],
    },
    chatBot: {
      enabled: true,
      key: 'bae76f5c925d4dd6b37748fd6b9c01010a2cd46fafd94b56813753bb4fd6b644',
      includePaths: ['/', '/service-hilfe'],
      excludePaths: [
        '/service-hilfe/newsletter/anmelden',
        '/service-hilfe/newsletter/abmelden',
        '/service-hilfe/nachhaltigkeit',
        '/service-hilfe/ueber-uns',
      ],
    },
  },
  { arrayMerge: customArrayMerge },
);

export const getFullLocale = (localeOrLanguage: string | undefined) =>
  getFullLocaleFromConfig(localeOrLanguage, config);

// eslint-disable-next-line import-x/no-default-export
export default config;
